import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  deletePaymentDetails,
  getAllPaymentDetails,
} from "../../api/PaymentDetails";
import "../../vendor/css/viewpaymentdetails.css";

const ViewPaymentDetails = () => {
  const [paymentDetailsList, setPaymentDetailsList] = useState([]);
  const [selectedPaymentDetails, setSelectedPaymentDetails] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const paymentsPerPage = 10;

  useEffect(() => {
    const getPaymentDetailsList = async () => {
      try {
        const response = await getAllPaymentDetails();
        const payments = response?.payments || response?.paymentDetails || response || [];
        setPaymentDetailsList(Array.isArray(payments) ? payments : []);
        const validPayments = Array.isArray(payments) ? payments : [];
        setTotalAmount(
          validPayments.reduce((acc, curr) => acc + (curr.amount || 0), 0)
        );
      } catch (error) {
        console.error("Error fetching payment details:", error);
        setPaymentDetailsList([]);
        setTotalAmount(0);
      } finally {
        setLoading(false);
      }
    };
    getPaymentDetailsList();
    const interval = setInterval(getPaymentDetailsList, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Filter payments based on search term
  const filteredPayments = (Array.isArray(paymentDetailsList) ? paymentDetailsList : []).filter(payment =>
    payment.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.phone_no?.includes(searchTerm) ||
    payment.payment_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastPayment = currentPage * paymentsPerPage;
  const indexOfFirstPayment = indexOfLastPayment - paymentsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstPayment, indexOfLastPayment);
  const totalPages = Math.ceil(filteredPayments.length / paymentsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleDeletePaymentDetails = (paymentDetailsToDelete) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deletePaymentDetails(paymentDetailsToDelete)
          .then((res) => {
            if (res.status === 200) {
              Swal.fire("Deleted!", "Your file has been deleted.", "success");
              setPaymentDetailsList((prev) =>
                prev.filter((item) => item._id !== paymentDetailsToDelete._id)
              );
              // Update total amount after deletion
              setTotalAmount((prev) =>
                prev - (paymentDetailsToDelete.amount || 0)
              );
            }
          })
          .catch((err) => {
            console.error("Delete failed:", err);
            Swal.fire("Error", "Failed to delete payment", "error");
          });
      }
    });
  };

  if (loading) {
    return (
      <div className="view-payments">
        <div className="payments-header">
          <h1>Payment Management</h1>
          <p>View and manage all payment transactions</p>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="view-payments">
      <div className="payments-header">
        <h1 style={{ fontFamily: 'serif' }}>Payment Management</h1>
        <p style={{ fontFamily: 'serif' }}>View and manage all payment transactions</p>
      </div>

      <div className="payments-stats">
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-money"></i>
          </div>
          <div className="stat-content">
            <h3>₹{totalAmount.toLocaleString()}</h3>
            <p>Total Revenue</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-receipt"></i>
          </div>
          <div className="stat-content">
            <h3>{paymentDetailsList.length}</h3>
            <p>Total Transactions</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <i className="bx bx-search"></i>
          </div>
          <div className="stat-content">
            <h3>{filteredPayments.length}</h3>
            <p>Filtered Results</p>
          </div>
        </div>
      </div>

      <div className="payments-controls">
        <div className="search-container">
          <i className="bx bx-search search-icon"></i>
          <input
            type="text"
            placeholder="Search by name, email, phone, or payment method..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="payments-table-container">
        <table className="payments-table">
          <thead>
            <tr>
              <th>Amount</th>
              <th>Payment Method</th>
              <th>Customer</th>
              <th>Contact Information</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPayments.length > 0 ? (
              currentPayments.map((payment, index) => (
                <tr key={payment._id || index}>
                  <td>
                    <div className="amount-cell">
                      <span className="currency">₹</span>
                      <span className="amount">{payment.amount?.toLocaleString()}</span>
                    </div>
                  </td>
                  <td>
                    <div className="payment-method">
                      <span className={`method-badge ${payment.payment_type?.toLowerCase()}`}>
                        {payment.payment_type}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="customer-name">
                      <div className="avatar">
                        {payment.name?.charAt(0).toUpperCase()}
                      </div>
                      <span>{payment.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <a href={`tel:${payment.phone_no}`} className="contact-link">
                        <i className="bx bx-phone"></i>
                        {payment.phone_no}
                      </a>
                      <a href={`mailto:${payment.email}`} className="contact-link">
                        <i className="bx bx-envelope"></i>
                        {payment.email}
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn btn-danger delete-btn"
                        onClick={() => handleDeletePaymentDetails(payment)}
                        title="Delete Payment"
                      >
                        <i className="bx bx-trash"></i>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  <div className="no-data-content">
                    <i className="bx bx-money-x"></i>
                    <p>No payments found</p>
                    <small>Try adjusting your search criteria</small>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-left"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => paginate(i + 1)}
              className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
            >
              {i + 1}
            </button>
          ))}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            <i className="bx bx-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewPaymentDetails;
